<template lang="pug">
    .shared-shop-data-banner(v-if="isVisible" :class="{ 'is-read-only': !canManage }")
        i.fas.shared-shop-data-banner__icon(:class="canManage ? 'el-icon-fa-code-branch' : 'el-icon-fa-lock'")
        .shared-shop-data-banner__text
            span.shared-shop-data-banner__title {{ canManage ? $t('Shared shop data') : $t('Shared shop data, read only') }}
            span(v-if="canManage")
                |  {{ $t('This data belongs to the shop "{shop}" and is shared with {count} stores. Changes apply to all of them.', { shop: shopName, count: storeCount }) }}
            span(v-else)
                |  {{ $t('This data belongs to the parent shop "{shop}". You do not have the rights to change it here.', { shop: shopName }) }}
        el-tooltip(placement="bottom-end")
            .shared-shop-data-banner__stores(slot="content")
                span(v-for="store in storeNames" :key="store") {{ store }}
            span.shared-shop-data-banner__hint {{ $t('Which stores?') }}
</template>

<i18n>
{
    "de": {
        "Shared shop data": "Geteilte Shop-Daten",
        "Shared shop data, read only": "Geteilte Shop-Daten, nur lesbar",
        "This data belongs to the parent shop \"{shop}\". You do not have the rights to change it here.": "Diese Daten gehören zum übergeordneten Shop \"{shop}\". Sie haben keine Rechte, sie hier zu ändern.",
        "This data belongs to the shop \"{shop}\" and is shared with {count} stores. Changes apply to all of them.": "Diese Daten gehören zum Shop \"{shop}\" und werden mit {count} Stores geteilt. Änderungen wirken sich auf alle aus.",
        "Which stores?": "Welche Stores?"
    },
    "en": {
        "This data belongs to the shop \"{shop}\" and is shared with {count} stores. Changes apply to all of them.": "This data belongs to the shop \"{shop}\" and is shared with {count} stores. Changes apply to all of them.",
        "This data belongs to the parent shop \"{shop}\". You do not have the rights to change it here.": "This data belongs to the parent shop \"{shop}\". You do not have the rights to change it here."
    }
}
</i18n>

<script>
import _ from 'lodash';
import { useSetupStore } from '@/stores';

export default {
    name: 'SharedShopDataBanner',
    computed: {
        activeCms() {
            return useSetupStore().activeCms;
        },
        storeNames() {
            return (_.get(this.activeCms, 'shop.cmses') || []).map(cms => cms.name).filter(Boolean);
        },
        storeCount() {
            return (_.get(this.activeCms, 'shop.cmses') || []).length;
        },
        // Whether the caller may write the shared data. Resolved by the API
        // against the OWNER store's rights - child-store rights do not count.
        canManage() {
            return _.get(this.activeCms, 'shop.can_manage_shared_data') !== false;
        },
        shopName() {
            return _.get(this.activeCms, 'shop.name') || _.get(this.activeCms, 'shop.ownerCms.name') || '';
        },
        // Only on screens whose data lives on the shop (route meta), and only
        // when that shop actually carries more than one store. A screen that is
        // only partly shared opts out with `sharedWithShopBanner: false` and marks
        // the shared sections itself - the permission gate is unaffected.
        isVisible() {
            if (_.get(this.$route, 'meta.sharedWithShopBanner') === false) return false;

            return !!_.get(this.$route, 'meta.sharedWithShop') && this.storeCount > 1;
        }
    }
};
</script>

<style lang="sass" scoped>
.shared-shop-data-banner
    background: #f4f7ff
    border: 1px solid #dbe4ff
    border-radius: 4px
    padding: 10px 16px
    margin: 0 0 25px 0
    display: flex
    align-items: center
    gap: 10px
    font-size: 13px
    line-height: 1.4

    &.is-read-only
        background: #fdf6ec
        border-color: #f5dab1

        .shared-shop-data-banner__icon,
        .shared-shop-data-banner__title,
        .shared-shop-data-banner__hint
            color: #E6A23C

        .shared-shop-data-banner__text
            color: #8a6d3b

    &__icon
        color: #409EFF
        font-size: 15px
        flex-shrink: 0

    &__text
        flex: 1
        color: #5a6b8c

    &__title
        font-weight: 600
        color: #409EFF

    &__hint
        color: #409EFF
        font-size: 12px
        white-space: nowrap
        cursor: help
        border-bottom: 1px dotted currentColor

    &__stores
        display: flex
        flex-direction: column
        gap: 2px
</style>
